import type { Option, SelectProps } from '@byecode/ui'
import { backgroundAndColorOnClick, Divider, Group, IconFont, Loading, Popover, Select, Text, Toast } from '@byecode/ui'
import type { CursorProtocol } from '@lighthouse/core'
import type { UploadDropZoneProps } from '@lighthouse/shared'
import {
    getImageFullUrlInApplication,
    ListItem4ByecodeUi,
    UploadDropZone,
    useAtomData,
    useUploadItemFinished,
    useUploadItemUploading
} from '@lighthouse/shared'
import { stopPropagation } from '@lighthouse/tools'
import { forwardRef, useCallback, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { lastPageOfStackAtom, pageAtomFamily } from '@/atoms/page/state'
import { useCurrentAppId } from '@/hooks/useApplication'
import { uploadManagerInAppParams } from '@/utils/auth'

const UploadButton = styled.button`
    width: 100%;
    height: 32px;
    padding: 0 16px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    ${backgroundAndColorOnClick};
`

const OPTIONS: Option[] = [
    {
        label: '自动',
        value: '1',
        children: [
            {
                label: '根据组件类型自动显示',
                icon: <IconFont type="ClickButton" size={16} fill="var(--color-gray-400)" />,
                value: 'auto'
            }
        ]
    },
    {
        label: '原生',
        value: '2',
        children: [
            {
                label: '黑色指针',
                icon: <IconFont type="mouse12" size={16} fill="var(--color-gray-400)" />,
                value: 'default'
            },
            {
                label: '手指',
                icon: <IconFont type="mouse11" size={16} fill="var(--color-gray-400)" />,
                value: 'pointer'
            },
            {
                label: '进行中',
                icon: <IconFont type="mouse10" size={16} fill="var(--color-gray-400)" />,
                value: 'progress'
            },
            {
                label: '复制',
                icon: <IconFont type="mouse09" size={16} fill="var(--color-gray-400)" />,
                value: 'copy'
            },
            {
                label: '禁止放置',
                icon: <IconFont type="mouse08" size={16} fill="var(--color-gray-400)" />,
                value: 'no-drop'
            },
            {
                label: '禁止操作',
                icon: <IconFont type="mouse07" size={16} fill="var(--color-gray-400)" />,
                value: 'not-allowed'
            },
            {
                label: '抓取',
                icon: <IconFont type="mouse06" size={16} fill="var(--color-gray-400)" />,
                value: 'grab'
            },
            {
                label: '放大',
                icon: <IconFont type="mouse05" size={16} fill="var(--color-gray-400)" />,
                value: 'zoom-in'
            },
            {
                label: '缩小',
                icon: <IconFont type="mouse04" size={16} fill="var(--color-gray-400)" />,
                value: 'zoom-out'
            },
            {
                label: '帮助',
                icon: <IconFont type="mouse03" size={16} fill="var(--color-gray-400)" />,
                value: 'help'
            },
            {
                label: '移动',
                icon: <IconFont type="mouse02" size={16} fill="var(--color-gray-400)" />,
                value: 'move'
            },
            {
                label: '无指针',
                icon: <IconFont type="mouse01" size={16} fill="var(--color-gray-400)" />,
                value: 'none'
            }
        ]
    }
]

interface Props {
    value?: CursorProtocol
    onChange?: (v: CursorProtocol) => void
}

export const CursorSetting = forwardRef<HTMLInputElement, Props>(({ value, onChange }, ref) => {
    const latestValue = useLatest(value)

    const appId = useCurrentAppId()
    const uploadId = useId()
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageName = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => s?.name || '', [])
    )

    const { t } = useTranslation()

    const uploadProps = useMemo<UploadDropZoneProps>(
        () => ({
            accept: 'image/cur, image/png, image/jpg',
            multiple: false,
            uploadOptions: {
                info: { id: uploadId, label: pageName, groupId: pageId },
                options: {
                    fileFilter: file => {
                        if (file instanceof File && file.size > 500 * 1000) {
                            Toast.error(t('imageUploadMaxLimitSizeWithKb', { size: 500 }))
                            return false
                        }
                        return true
                    },
                    ...uploadManagerInAppParams()
                }
            }
        }),
        [pageId, pageName, t, uploadId]
    )

    const [loading, setLoading] = useState(false)

    useUploadItemUploading(uploadId, () => {
        setLoading(true)
    })
    useUploadItemFinished(uploadId, item => {
        setLoading(false)

        const url = item.uploadResponse?.data?.content?.url
        if (!url) {
            return
        }

        onChange?.({ ...latestValue.current, type: 'custom', url, value: undefined })
    })

    const dropdownProps = useMemo<SelectProps['dropdownProps']>(() => {
        return {
            extra: (
                <>
                    <Divider mb={8} />
                    <UploadDropZone {...uploadProps}>
                        <UploadButton>
                            <IconFont type="UploadSimple" size={16} fill="var(--color-gray-400)" />
                            上传本地图片
                            <Popover
                                trigger="hover"
                                width={260}
                                withinPortal
                                position="left-end"
                                offsetOptions={{ mainAxis: 136, crossAxis: 16 }}
                            >
                                <Popover.Target>
                                    <IconFont type="Question" size={16} fill="var(--color-gray-400)" />
                                </Popover.Target>
                                <Popover.Dropdown
                                    onClick={stopPropagation}
                                    styles={{
                                        dropdown: {
                                            padding: '8px 4px',
                                            background: 'var(--color-black)',
                                            border: '1px solid var(--color-black)',
                                            fontSize: 14,
                                            color: 'var(--color-white)'
                                        }
                                    }}
                                >
                                    格式限制： 只能使用 cur、png、jpg 格式的文件。
                                    <br />
                                    尺寸限制： 最大为 128x128 像素（推荐使用 32x32 或 64x64）大图或非正方形图像会被忽略。 <br />
                                    大小限制： 不能上传超过 500KB 的图片。
                                </Popover.Dropdown>
                            </Popover>
                        </UploadButton>
                    </UploadDropZone>
                </>
            )
        }
    }, [uploadProps])

    const handleChange = useCallback(
        (v: string) => {
            onChange?.({ ...latestValue.current, type: 'system', value: v })
        },
        [latestValue, onChange]
    )

    const prefixDom = useMemo(() => {
        if (loading) {
            return <Loading outlined size={16} />
        }

        if (value?.type === 'custom' && value.url) {
            return <img src={getImageFullUrlInApplication(appId, value.url)} width={16} height={16} alt="custom cursor" />
        }
    }, [appId, loading, value?.type, value?.url])

    const placeholder = useMemo(() => {
        if (value?.type === 'custom' && value.url) {
            return value.url
        }
    }, [value?.type, value?.url])

    return (
        <Group
            mode="switch"
            label="自定义光标"
            opened={value?.enable}
            defaultOpen={false}
            onCollapseChange={v => {
                // 初次打开，添加默认值
                if (v && !latestValue.current) {
                    onChange?.({ type: 'system', value: 'auto', enable: v })
                    return
                }

                onChange?.({ type: 'system', ...latestValue.current, enable: v })
            }}
        >
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <Text>光标样式</Text>
                <Select
                    ref={ref}
                    prefix={prefixDom}
                    value={value?.value}
                    onChange={handleChange}
                    options={OPTIONS}
                    dropdownProps={dropdownProps}
                    placeholder={placeholder}
                    styles={{ root: { width: 180 }, placeholder: placeholder ? { color: '$colorBlack' } : undefined }}
                />
            </ListItem4ByecodeUi>
        </Group>
    )
})
